import { Waypoint } from 'react-waypoint';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';

interface IHeapContent {
  name: string;
  query: string;
}

const HeapWaypoint = (heapContent: IHeapContent) => {
  const [parent, setParent] = useState<HTMLElement | null>(null);
  const [childId, setChildId] = useState<string>('');
  const observerRef = useRef<MutationObserver | null>(null);
  const attemptsRef = useRef<number>(0);
  const maxAttempts = 10; // ~5s with 500ms polling fallback

  function setParentAndChild(el: HTMLElement) {
    setParent(el);
    if (el.children?.length > 0) {
      const firstChild = el.children[0] as HTMLElement;
      setChildId(firstChild.id);
    }
  }

  useEffect(() => {
    if (parent) return; // already found

    const tryFind = () => {
      attemptsRef.current += 1;
      try {
        const elements = document.querySelectorAll(heapContent.query);
        if (elements.length > 0) {
          setParentAndChild(elements[0] as HTMLElement);
          if (observerRef.current) observerRef.current.disconnect();
          return true;
        }
      } catch (e) {
        // do nothing
      }
      if (attemptsRef.current === maxAttempts) {
        console.warn(
          `HeapWaypoint: element not found for selector '${heapContent.query}' after ${maxAttempts} attempts.`,
        );
      }
      return false;
    };

    if (tryFind()) return;

    // MutationObserver for dynamic insertion
    try {
      observerRef.current = new MutationObserver(() => {
        tryFind();
      });
      observerRef.current.observe(document.body, { childList: true, subtree: true });
    } catch {}

    // Polling fallback (covers cases where mutations happen outside observer scope or race conditions)
    const interval = setInterval(() => {
      if (parent) return; // found meanwhile
      if (tryFind()) clearInterval(interval);
      if (attemptsRef.current >= maxAttempts) clearInterval(interval);
    }, 500);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heapContent.query, parent]);

  async function trackImpression() {
    const window: IWindow = global.window;
    if (window.heap && heapContent?.name) {
      window.heap.track(heapContent.name, { ad_id: childId });
      console.log('tracked ' + heapContent.name + (childId ? ` (childId: ${childId})` : ''));
    }
  }

  return parent ? createPortal(<Waypoint scrollableAncestor={window} onEnter={trackImpression} />, parent) : null;
};

const HeapWaypointManager = () => {
  const [waypoints, setWaypoints] = useState<IHeapContent[]>([]);
  const heapWaypoints = (window as any)?.heapContent as IHeapContent[];

  useEffect(() => {
    if (heapWaypoints && heapWaypoints.length > 0) {
      setWaypoints(heapWaypoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {waypoints.map((heapContent: IHeapContent) => (
        <HeapWaypoint key={heapContent.name || heapContent.query} {...heapContent} />
      ))}
    </>
  );
};

export default HeapWaypointManager;
